import { useDispatch, useSelector } from "react-redux";
import { isActionOf } from "typesafe-actions";

import type { ShortOpportunityList } from "app/pages/sales-intelligence/modules/lists/types/types";

import type { ListDeleteModalProps } from "../../components/modals/ListDeleteModal/ListDeleteModal";
import useCurrentStaticList from "../../hooks/useCurrentStaticList";
import { deleteOpportunityListAsync, listDeleteModalOpenSetAction } from "../../store/action-creators";
import { deleteOpportunityListThunk } from "../../store/effects";
import { selectOpportunityListDeleteModalIsOpen, selectOpportunityListDeleting } from "../../store/selectors";
import type { OpportunityListType } from "../../types";

import ListDeleteModalTracked from "./ListDeleteModalTrackedContainer";

type ListDeleteModalContainerProps = {
    onAfterDelete?(id: OpportunityListType["opportunityListId"]): void;
} & Partial<Pick<ListDeleteModalProps, "onCancel" | "onSubmit">>;

const ListDeleteModalContainer = (props: ListDeleteModalContainerProps) => {
    const dispatch = useDispatch();
    const isOpen = useSelector(selectOpportunityListDeleteModalIsOpen);
    const isBeingDeleted = useSelector(selectOpportunityListDeleting);
    const list = useCurrentStaticList();

    async function handleDeleteSubmit(id: OpportunityListType["opportunityListId"]) {
        props.onSubmit?.(id);

        const resultAction = await dispatch(deleteOpportunityListThunk(id));
        const isSuccessfulAction = isActionOf(deleteOpportunityListAsync.success);

        if (isSuccessfulAction(resultAction)) {
            dispatch(listDeleteModalOpenSetAction(false));
            props.onAfterDelete?.(id);
        }
    }

    function handleCancel() {
        props.onCancel?.();
        dispatch(listDeleteModalOpenSetAction(false));
    }

    const mappedList = (list?: Readonly<OpportunityListType>): Readonly<ShortOpportunityList> | undefined => {
        if (list) {
            const { opportunityListId, listType, friendlyName, country, opportunities } = list;
            return {
                id: opportunityListId,
                listType,
                totalCount: opportunities.length,
                friendlyName,
                country,
            } as ShortOpportunityList;
        }

        return;
    };

    return (
        <ListDeleteModalTracked
            list={mappedList(list)}
            isOpen={isOpen}
            isBeingSubmitted={isBeingDeleted}
            onCancel={handleCancel}
            onSubmit={handleDeleteSubmit}
        />
    );
};

export default ListDeleteModalContainer;
